import React, { useState } from 'react';
import Toast from 'react-native-toast-message';
import { translate } from '../../../services/translate';
import { NativeSyntheticEvent, TextInputKeyPressEventData, View } from 'react-native';
import { cameraService } from '../../../services/central-api/cameras';
import { useAuth } from '../../../context/Auth';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { CamerasParamList } from '../../../typings/Params';
import useFilters from '../../../components/Filter/hooks/useFilters';
import FilterInput from '../../../components/Filter/components/FilterInput';
import MyDataTable from '../../../components/MyDataTable';
import AlertModal from '../../../components/AlertModal';
import { TranslateRich } from '../../../components/TranslateRich';
import { Pagination } from '../../../components/Pagination';
import CustomButton from '../../../components/CustomButton';
import CameraTagModal from './CameraTagModal';
import DropdownButton from '../../../components/DropdownButton';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';
import { RootNavigation } from '../../../Routes';

interface TagsParams {
    navigation: RootNavigation;
}

export default function CameraTags({ navigation }: TagsParams) {
    const { user: authenticatedUser } = useAuth();

    const route = useRoute<RouteProp<CamerasParamList, 'CameraTags'>>();
    const [filters, setFilters] = useFilters<CamerasParamList['CameraTags']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [selectedTag, setSelectedTag] = useState<CameraTag>();
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['camerasTags', filters],
        queryFn: () => cameraService.getCameraTags(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    async function deleteTag(tag: BaseCameraTag) {
        try {
            await cameraService.deleteCameraTag(tag.id);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            setIsDeleteModalVisible(false);
            refetch();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function handleFormClose() {
        setSelectedTag(undefined);
        setModalVisible(false);
        refetch();
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    return (
        <Container>
            <Header
                title={translate('cameraTags')}
                actions={
                    (authenticatedUser?.isAdmin || authenticatedUser?.permissions.camera_manage_tags) &&
                    <CustomButton text={translate('register')} onPress={() => {
                        setSelectedTag(undefined);
                        setModalVisible(true);
                    }} />
                }
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredCameraTags')}
                    zIndex={2}
                    filterInput={<FilterInput
                        value={textFilter}
                        onChange={setTextFilter}
                        placeholder={translate('typeToSearch')}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: 'ID',
                                selector: row => row.id,
                                grow: 0
                            },
                            {
                                name: translate('color'),
                                grow: 0,
                                cell: row => <View style={{ backgroundColor: row.color, height: 20, width: 20, borderRadius: 4 }}></View>
                            },
                            {
                                name: translate('name'),
                                selector: row => row.name,
                                wrap: true,
                                grow: 4
                            },
                            {
                                name: translate('associatedCameras'),
                                selector: row => row.associatedCount,
                                wrap: true,
                            },
                            {
                                cell: row => (
                                    <DropdownButton
                                        actions={[
                                            {
                                                content: translate('editInformation'),
                                                onPress: () => {
                                                    setSelectedTag(row);
                                                    setModalVisible(true);
                                                }
                                            },
                                            {
                                                content: translate('delete'),
                                                variant: 'danger',
                                                onPress: () => {
                                                    setSelectedTag(row);
                                                    setIsDeleteModalVisible(true);
                                                },
                                            }
                                        ]}
                                    />
                                ),
                                wrap: true,
                                button: true
                            }
                        ]}
                        data={data?.rows ?? []}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        totalItems={data?.count ?? 0}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>
            <CameraTagModal
                isModalVisible={isModalVisible}
                setModalVisible={setModalVisible}
                cameraTag={selectedTag}
                onClose={handleFormClose}
            />
            {selectedTag && <AlertModal
                onSubmit={async () => {
                    try {
                        await deleteTag(selectedTag);
                    } catch (err) {
                        console.error(err);
                    }
                }}
                isModalVisible={isDeleteModalVisible}
                setModalVisible={() => setIsDeleteModalVisible(false)}
                title={translate('deleteCameraTag')}
                description={<>
                    <TranslateRich
                        i18nKey='deleteCameraTagDescription'
                        values={{ tagName: <strong>{selectedTag.name}</strong> }}
                    />
                    <br />
                    <br />
                    <TranslateRich i18nKey='proceedWithDeletion' />
                </>}
                customButtonLabel={translate('yesDelete')}
                type='destructive'
            />}

        </Container>
    );
}
